import { NavLink, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CalendarDays,
  Car,
  ClipboardList,
  Cog,
  CreditCard,
  DollarSign,
  LogOut,
  Package,
  Shield,
  UserCircle,
  UserCheck,
  Users,
  Wrench,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import BrandLogo from '../shared/BrandLogo';
import { useLocalization } from '../../hooks/useLocalization';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';

const profileLabels = {
  super_admin: 'Suporte GDN',
  gdn_admin: 'Gestão GDN',
  admin: 'Administrador',
  mecanico: 'Funcionário',
};

export default function Sidebar({ variant = 'client' }) {
  const { t } = useLocalization();
  const { user, perfil, signOut } = useAuth();
  const { features: planFeatures } = usePlanFeatures();
  const navigate = useNavigate();
  const isAdmin = perfil?.perfil === 'admin';
  const canManageCatalog = ['admin', 'mecanico'].includes(perfil?.perfil);
  const isGdnAdmin = ['gdn_admin', 'super_admin'].includes(perfil?.perfil) || perfil?.metadata?.is_gdn_admin;
  const isGdnMode = variant === 'gdn';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const navItem = (to, icon, label, badge) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex min-w-[72px] flex-col items-center justify-center gap-1 rounded-lg px-2 py-2 text-center text-[11px] font-medium transition-all lg:min-w-0 lg:flex-row lg:justify-start lg:gap-3 lg:px-3 lg:py-2.5 lg:text-left lg:text-sm ${
          isActive
            ? 'bg-orange-500/15 text-orange-400 lg:border-l-2 lg:border-orange-500'
            : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
        }`
      }
    >
      {icon}
      <span className="w-full truncate lg:flex-1">{label}</span>
      {badge > 0 && (
        <span className="ml-auto rounded-full bg-orange-500 px-1.5 py-0.5 text-xs font-bold text-black">
          {badge}
        </span>
      )}
    </NavLink>
  );

  return (
    <aside className="fixed bottom-0 left-0 right-0 z-40 flex h-[4.25rem] w-full border-t border-border bg-surface/95 backdrop-blur lg:top-0 lg:h-screen lg:w-60 lg:flex-col lg:border-r lg:border-t-0 lg:bg-surface">
      <div className="hidden border-b border-border px-5 py-6 lg:block">
        <BrandLogo size="sm" />
        <div className="mt-0.5 text-[10px] uppercase tracking-widest text-zinc-500">
          {isGdnMode ? 'Backoffice GDN' : 'Gestão Inteligente'}
        </div>
      </div>

      <nav className="flex flex-1 snap-x gap-1 overflow-x-auto px-2 py-2 [-webkit-overflow-scrolling:touch] lg:block lg:snap-none lg:space-y-1 lg:overflow-y-auto lg:px-3 lg:py-4">
        {isGdnMode ? (
          <>
            <div className="hidden px-3 pb-1 text-[9px] uppercase tracking-widest text-zinc-600 lg:block">GDN Corp</div>
            {navItem('/gdn/clientes', <Shield size={16} />, 'Painel GDN')}
            {navItem('/gdn/planos', <DollarSign size={16} />, 'Planos e limites')}
            {navItem('/configuracoes/pagamento', <CreditCard size={16} />, 'Pagamentos')}
          </>
        ) : (
          <>
            {isGdnAdmin ? (
              <>
                <div className="hidden px-3 pb-1 text-[9px] uppercase tracking-widest text-zinc-600 lg:block">GDN Corp</div>
                {navItem('/gdn/clientes', <Shield size={16} />, 'Painel GDN')}
                {navItem('/gdn/planos', <DollarSign size={16} />, 'Planos e limites')}
                {navItem('/configuracoes/pagamento', <CreditCard size={16} />, 'Pagamentos')}
              </>
            ) : (
              <>
                <div className="hidden px-3 pb-1 text-[9px] uppercase tracking-widest text-zinc-600 lg:block">{t('main')}</div>
                {navItem('/dashboard', <BarChart3 size={16} />, t('dashboard'))}
                {navItem('/perfil', <UserCircle size={16} />, t('profile'))}
                {navItem('/ordens-servico', <ClipboardList size={16} />, t('serviceOrders'))}
                {navItem('/agenda', <CalendarDays size={16} />, t('schedule'))}

                <div className="hidden px-3 pb-1 pt-3 text-[9px] uppercase tracking-widest text-zinc-600 lg:block">{t('records')}</div>
                {navItem('/clientes', <Users size={16} />, t('customers'))}
                {navItem('/veiculos', <Car size={16} />, t('vehicles'))}
                {canManageCatalog && navItem('/servicos', <Wrench size={16} />, t('services'))}
                {isAdmin && navItem('/estoque', <Package size={16} />, t('inventory'))}

                {isAdmin && (
                  <>
                    <div className="hidden px-3 pb-1 pt-3 text-[9px] uppercase tracking-widest text-zinc-600 lg:block">{t('management')}</div>
                    {navItem('/financeiro', <DollarSign size={16} />, t('finance'))}
                    {planFeatures.teamManagement && navItem('/equipe', <UserCheck size={16} />, t('team'))}
                    {navItem('/configuracoes', <Cog size={16} />, t('settings'))}
                  </>
                )}
              </>
            )}
          </>
        )}
      </nav>

      <div className="hidden border-t border-border px-3 py-4 lg:block">
        <div className="flex items-center gap-3 rounded-lg bg-zinc-800/50 px-3 py-2">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-red-600 text-xs font-bold text-white">
            {user?.email?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-semibold text-white">{user?.email?.split('@')[0]}</div>
            <div className="text-[10px] text-zinc-500">{profileLabels[perfil?.perfil] || 'Usuário'}</div>
          </div>
          <button onClick={handleSignOut} className="text-zinc-500 transition-colors hover:text-red-400">
            <LogOut size={14} />
          </button>
        </div>
        <div className="mt-2 text-center text-[9px] text-zinc-700">by GDN Corp</div>
      </div>
    </aside>
  );
}
